import { useEffect, useMemo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import JsonLd from '../../components/feature/JsonLd';

const ServicesPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    document.title = 'BPO Services – AI Call Center Outsourcing & Nearshore Solutions | Call Center Pros';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Explore Call Center Pros AI-powered BPO services: customer support, technical help, sales, lead generation, live chat and data entry via nearshore outsourcing. Get a free quote.');
  }, []);

  const servicesPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'BPO Services – AI Call Center Outsourcing & Nearshore Solutions',
    url: `${siteUrl}/services`,
    description: 'Comprehensive AI-powered BPO nearshore services including customer service, technical support, sales, lead generation, and more.',
    provider: {
      '@type': 'Organization',
      name: 'Call Center Pros',
      url: siteUrl,
    },
    mainEntity: {
      '@type': 'ItemList',
      name: 'BPO Services',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Customer Service', description: 'Deliver exceptional customer experiences with 24/7 trained support teams.' },
        { '@type': 'ListItem', position: 2, name: 'Technical Support', description: 'Expert technical assistance with skilled agents trained in troubleshooting.' },
        { '@type': 'ListItem', position: 3, name: 'Sales Support', description: 'Boost revenue with professional sales teams trained in consultative selling.' },
        { '@type': 'ListItem', position: 4, name: 'Lead Generation', description: 'Generate high-quality leads with targeted outreach campaigns.' },
        { '@type': 'ListItem', position: 5, name: 'Data Entry', description: 'Accurate and efficient data processing services.' },
        { '@type': 'ListItem', position: 6, name: 'Live Chat Support', description: 'Real-time customer engagement through live chat channels.' },
      ],
    },
  }), [siteUrl]);

  const services = [
    {
      icon: 'ri-customer-service-2-line',
      title: 'Customer Service',
      description: 'Deliver exceptional customer experiences with our trained support teams available 24/7 to handle inquiries, complaints, and support requests.',
      features: ['24/7 Availability', 'Multi-channel Support', 'CRM Integration', 'Quality Assurance']
    },
    {
      icon: 'ri-tools-line',
      title: 'Technical Support',
      description: 'Expert technical assistance for your products and services with skilled agents trained in troubleshooting and problem resolution.',
      features: ['Level 1-3 Support', 'Remote Assistance', 'Ticket Management', 'Knowledge Base']
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Sales Support',
      description: 'Boost your revenue with our professional sales teams trained in consultative selling and relationship building.',
      features: ['Inbound Sales', 'Upselling/Cross-selling', 'Lead Qualification', 'CRM Updates']
    },
    {
      icon: 'ri-user-search-line',
      title: 'Lead Generation',
      description: 'Generate high-quality leads for your business with our targeted outreach campaigns and qualification processes.',
      features: ['Outbound Calling', 'Email Campaigns', 'Lead Scoring', 'Appointment Setting']
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Data Entry',
      description: 'Accurate and efficient data processing services to help you manage and organize your business information.',
      features: ['Data Processing', 'Database Management', 'Document Digitization', 'Quality Control']
    },
    {
      icon: 'ri-chat-3-line',
      title: 'Live Chat Support',
      description: 'Real-time customer engagement through live chat channels to provide instant support and increase conversions.',
      features: ['Real-time Response', 'Multi-chat Handling', 'Proactive Engagement', 'Chat Analytics']
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Discovery Call',
      description: 'We learn about your business needs, challenges, and goals to design the perfect solution.'
    },
    {
      number: '02',
      title: 'Custom Proposal',
      description: 'Receive a tailored proposal with pricing, timeline, and service specifications within 24 hours.'
    },
    {
      number: '03',
      title: 'Team Setup',
      description: 'We recruit, train, and onboard your dedicated team with your processes and systems.'
    },
    {
      number: '04',
      title: 'Launch & Scale',
      description: 'Go live with continuous monitoring, optimization, and the ability to scale as you grow.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <JsonLd id="services-page-schema" data={servicesPageSchema} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-6">
              Our Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">BPO Solutions</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              From customer service to technical support, we provide end-to-end BPO outsourcing solutions tailored to your business needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact-us" 
                className="inline-block bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </a>
              <a 
                href="/plans-and-pricing" 
                className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all border-2 border-blue-600 whitespace-nowrap cursor-pointer"
              >
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <i className="ri-check-line text-blue-600 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
                How It Works
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple Onboarding Process</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Get started with our BPO services in just 4 easy steps
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                    <div className="text-5xl font-bold text-blue-100 mb-4">{step.number}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-300 to-transparent"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-6">
                  Why Choose Us
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">The Call Center Pros Advantage</h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  We combine cutting-edge technology with experienced professionals to deliver exceptional results for your business.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-check-line text-blue-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Nearshore Advantage</h4>
                      <p className="text-gray-600 text-sm">Same time zones, cultural alignment, and easy communication</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-check-line text-blue-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">AI-Powered Solutions</h4>
                      <p className="text-gray-600 text-sm">Leverage machine learning for better efficiency and insights</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-check-line text-blue-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Scalable Teams</h4>
                      <p className="text-gray-600 text-sm">Quickly scale up or down based on your business needs</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <i className="ri-check-line text-blue-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Cost Savings</h4>
                      <p className="text-gray-600 text-sm">Reduce operational costs by up to 60% without compromising quality</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl transform -rotate-3"></div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/e2cfb0ef16db0563ae08543c3ee68dc5.jpg" 
                  alt="Call Center Operations" 
                  className="relative w-full h-auto rounded-2xl shadow-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Transform Your Operations?</h2>
          <p className="text-blue-100 mb-10 max-w-2xl mx-auto text-lg">
            Let's discuss how our BPO services can help you scale efficiently and reduce costs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact-us" 
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Free Consultation
            </a>
            <a 
              href="/plans-and-pricing" 
              className="inline-block bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all whitespace-nowrap cursor-pointer"
            >
              View Pricing Plans
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;